import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LocalMallOutlinedIcon from "@mui/icons-material/LocalMallOutlined";
import SignalCellularAltOutlinedIcon from "@mui/icons-material/SignalCellularAltOutlined";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Link from "@mui/material/Link";
import MenuItem from "@mui/material/MenuItem";
import MenuList from "@mui/material/MenuList";
import Typography from "@mui/material/Typography";
import { useNavigate, useRouterState } from "@tanstack/react-router";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useAnalyticsTracker } from "~/app/hooks/analytics/useAnalyticsTracker";
import ScopelyName from "~/assets/scopely_logo_name.webp";
import WofLogo from "~/assets/wof_logo.webp";
import { HELP_CENTER_URL, PRIVACY_POLICY_URL, TERMS_SERVICES_URL } from "~/constants/urls";

export function DrawerLeft({ handleCloseMenu }: { handleCloseMenu?: () => void }) {
  const navigate = useNavigate();
  const router = useRouterState();
  const { t } = useTranslation();
  const trackUI = useAnalyticsTracker("sys.ui");

  // Handle internal navigation with tracking
  const handleClick = (screen_id: string, to: string) => {
    trackUI({
      screen_id,
      screen_name: "web_portal",
      screen_type: "screen",
      screen_category: "web_portal",
      screen_subcategory: "web_portal",
      screen_content: "open",
      screen_section: null,
      interaction_type: "click",
      interaction_object: screen_id,
      interaction_object_category: "display",
      flow_name: "web_portal",
      flow_order: 2,
    });
    navigate({ to });
    if (handleCloseMenu) {
      handleCloseMenu();
    }
  };

  // Tracking for external links
  const linkTracking = (screen_id: string) => ({
    onClick: () => {
      trackUI({
        screen_id,
        screen_name: "web_portal",
        screen_type: "screen",
        screen_category: "web_portal",
        screen_subcategory: "web_portal",
        screen_section: null,
        screen_content: "open",
        interaction_type: "click",
        interaction_object: screen_id,
        interaction_object_category: "display",
        flow_name: "web_portal",
        flow_order: 2,
      });
      if (handleCloseMenu) {
        handleCloseMenu();
      }
    },
  });

  return (
    <Card
      sx={{
        height: "100%",
        minWidth: { xs: "", md: "240px" },
        maxWidth: "240px",
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <CardContent
        style={{ paddingBottom: "64px" }}
        sx={{
          flex: "1 1 100%",
          display: "flex",
          flexDirection: "column",
          py: 8,
          px: 2,
        }}
      >
        <Box
          width="100%"
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          gap={2}
          sx={{ flex: "1 1 100%" }}
        >
          <Box width="100%" display="flex" flexDirection="column" justifyContent="center" gap={4}>
            <Box display="flex" justifyContent="center">
              <img
                src={WofLogo}
                alt="logo"
                style={{
                  borderRadius: "28px",
                  width: "120px",
                  height: "120px",
                  boxShadow:
                    "0px 53px 15px 0px rgba(57, 32, 83, 0.00), 0px 34px 13px 0px rgba(57, 32, 83, 0.03), 0px 19px 11px 0px rgba(57, 32, 83, 0.11), 0px 8px 8px 0px rgba(57, 32, 83, 0.18), 0px 2px 5px 0px rgba(57, 32, 83, 0.21)",
                }}
              />
            </Box>

            <MenuList
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: 1,
              }}
            >
              <MenuItem
                onClick={() => handleClick("home_button", "/home")}
                selected={router.location.pathname === "/home"}
              >
                <HomeOutlinedIcon />
                {t("home")}
              </MenuItem>

              <MenuItem
                onClick={() => handleClick("leaderboard_button", "/leaderboards")}
                selected={router.location.pathname === "/leaderboards"}
              >
                <SignalCellularAltOutlinedIcon />
                {t("leaderboard.name")}
              </MenuItem>

              <MenuItem
                onClick={() => handleClick("store_button", "/store")}
                selected={router.location.pathname === "/store"}
              >
                <LocalMallOutlinedIcon />
                {t("store")}
              </MenuItem>

              <MenuItem
                component={Link}
                href={HELP_CENTER_URL}
                target="_blank"
                underline="none"
                {...linkTracking("helpcenter_button")}
              >
                <AutoStoriesOutlinedIcon />
                {t("help-center")}
              </MenuItem>
            </MenuList>
          </Box>

          <Box display="flex" flexDirection="column" gap={6}>
            <Box
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              gap={1}
            >
              <Link
                href={TERMS_SERVICES_URL}
                target="_blank"
                underline="none"
                {...linkTracking("terms_conditions")}
              >
                <Typography fontSize="16px" fontWeight="700" lineHeight="24px" color="#382053">
                  {t("terms-service")}
                </Typography>
              </Link>

              <Link
                href={PRIVACY_POLICY_URL}
                target="_blank"
                underline="none"
                {...linkTracking("privacy_policy")}
              >
                <Typography fontSize="16px" fontWeight="700" lineHeight="24px" color="#382053">
                  {t("privacy-policy")}
                </Typography>
              </Link>

              <Link
                component="button"
                className="ot-sdk-show-settings"
                underline="none"
                textAlign="left"
                onClick={() => {
                  trackUI({
                    screen_id: "cookies_settings",
                    screen_name: "web_portal",
                    screen_type: "screen",
                    screen_category: "web_portal",
                    screen_subcategory: "web_portal",
                    screen_section: null,
                    screen_content: "open",
                    interaction_type: "click",
                    interaction_object: "cookies_settings",
                    interaction_object_category: "display",
                    flow_name: "web_portal",
                    flow_order: 2,
                  });
                  window.UC_UI?.showSecondLayer();
                  if (handleCloseMenu) {
                    handleCloseMenu();
                  }
                }}
              >
                <Typography fontSize="16px" fontWeight="700" lineHeight="24px" color="#382053">
                  {t("cookies-settings")}
                </Typography>
              </Link>
            </Box>

            <Box display="flex" justifyContent="center" alignItems="center">
              <img src={ScopelyName} alt="Scopely Logo" style={{ width: "80%" }} />
            </Box>

            <Box display="flex" flexDirection="column" gap={3}>
              <Typography fontSize="12px" fontWeight="600" lineHeight="16px" color="#382053">
                "Wheel of Fortune®" is a registered trademark of Califon Productions, Inc. All
                Rights Reserved.
              </Typography>

              <Typography fontSize="12px" fontWeight="600" lineHeight="16px" color="#382053">
                © 2025 Califon Productions, Inc. All Rights Reserved.
              </Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
